import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Sparkles, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/lib/dates";
import DateField from "@/components/forms/DateField";

/**
 * AI spend: a window you choose, and the curve behind it.
 *
 * **Why the budget gauge does not follow the period selector.** Everything in
 * this panel answers the window the admin picked, except the gauge, which
 * always answers the current calendar month. The ceiling IS monthly and binary
 * (DANY.md §2.6) — it is the number the enforcer bills against and trips the
 * kill switch on. Dividing a 90-day total by a monthly ceiling would draw a
 * 300% bar and read as an emergency; dividing last month's by it would report
 * an alert that has already passed. The gauge says "this month" in its own
 * label so the two windows on screen are never confused for one.
 *
 * **Nothing is summed here.** Every number arrives already aggregated by
 * Postgres (backend/services/ai_usage.py). The browser's job is the shape.
 */
// The ceiling's month is a UTC month, so its days are UTC days: a step reached
// at 01:00 UTC on the 1st belongs to the 1st, not to a US evening of the 31st.
const dayLabel = (value) => formatDate(value, { timeZone: "UTC" });

export default function AiUsagePanel({ t }) {
  const step = (s) => t(`adminX.aiUsage.steps.${s}`, { defaultValue: s });
  const [period, setPeriod] = useState("current_month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { period };
    if (period === "custom") {
      if (!custom.start || !custom.end) { setLoading(false); return; }
      params.start = custom.start;
      params.end = custom.end;
    }
    try {
      const { data: d } = await api.get("/admin/ai-usage/summary", { params });
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [period, custom.start, custom.end, t]);

  useEffect(() => { load(); }, [load]);

  // The series is period-independent — it is the whole history — so it is
  // fetched once and not refetched when the window changes.
  useEffect(() => {
    api.get("/admin/ai-usage/series")
      .then(({ data: d }) => setSeries(d))
      .catch(() => setSeries({ months: [], budget_usd: 0 }));
  }, []);

  const PERIODS = ["current_month", "previous_month", "last_30d", "last_90d", "custom"];
  const budget = data?.budget;
  const steps = budget?.steps;
  const burn = budget?.burn;

  return (
    <div className="mt-10" data-testid="ai-usage-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl font-bold text-worker flex items-center gap-2">
          <Sparkles className="text-hive-ink" /> {t("adminX.dashboard.aiUsage")}
        </h2>
        {budget?.kill_switch_active && (
          <span className="text-xs px-2 py-1 rounded-sm bg-red-100 text-red-800 font-display">
            {t("adminX.dashboard.killSwitch")}
          </span>
        )}
      </div>

      {/* Filters in one row above everything they filter. */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="ai-period-filter">
        {PERIODS.map((key) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`px-3 py-1.5 rounded-sm text-xs font-display font-semibold transition ${
              period === key
                ? "bg-worker text-cream"
                : "bg-cream border border-sand text-worker hover:bg-linen"
            }`}
            data-testid={`ai-period-${key}`}
          >
            {t(`adminX.aiUsage.period.${key}`)}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2" data-testid="ai-custom-range">
            <div className="w-40">
              <DateField
                value={custom.start} max={custom.end || undefined}
                onChange={(start) => setCustom((c) => ({ ...c, start }))}
                className="input-base py-1 text-xs" data-testid="ai-custom-start"
                aria-label={t("adminX.aiUsage.from")}
              />
            </div>
            <span className="text-xs text-smoke">{t("adminX.aiUsage.to")}</span>
            <div className="w-40">
              <DateField
                value={custom.end} min={custom.start || undefined}
                onChange={(end) => setCustom((c) => ({ ...c, end }))}
                className="input-base py-1 text-xs" data-testid="ai-custom-end"
                aria-label={t("adminX.aiUsage.to")}
              />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-800"
             data-testid="ai-period-error">
          <AlertTriangle size={14} className="inline mr-1" /> {error}
        </div>
      )}

      {budget?.alert_80pct && !budget?.kill_switch_active && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-900"
             data-testid="ai-budget-alert">
          <AlertTriangle size={14} className="inline mr-1" />
          <strong>{t("adminX.dashboard.budgetAlert")}:</strong>{" "}
          {t("adminX.dashboard.budgetAlertBody", {
            pct: budget.spent_pct, spent: budget.spent_usd, budget: budget.budget_usd,
          })}
        </div>
      )}

      {loading && !data && (
        <div className="card-base p-8 mt-4 flex justify-center">
          <Loader2 className="animate-spin text-smoke" />
        </div>
      )}

      {data && (
        <>
          <div className={`mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
            {/* The gauge — always this month, and it says so. */}
            <div className="card-base p-5">
              <p className="label-base mb-0">{t("adminX.aiUsage.budgetThisMonth")}</p>
              <p className="font-mono text-3xl font-bold text-worker mt-1">
                ${budget.spent_usd}
                <span className="text-base text-smoke"> / ${budget.budget_usd}</span>
              </p>
              {/* The bar, and a tick at each of the ceiling's steps (DY1.7)
                  so "how far to the next one" is read off the bar rather than
                  computed from four numbers in the settings page. */}
              <div className="mt-3 relative">
                <div className="w-full bg-sand rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      budget.spent_pct >= (steps?.pcts?.backup ?? 80) ? "bg-danger"
                        : budget.spent_pct >= (steps?.pcts?.alert ?? 50) ? "bg-amber-500" : "bg-hive-ink"
                    }`}
                    style={{ width: `${Math.min(100, budget.spent_pct)}%` }}
                  />
                </div>
                {steps && Object.entries(steps.pcts).filter(([, v]) => v < 100).map(([step, v]) => (
                  <span key={step} aria-hidden="true"
                    className="absolute -top-0.5 h-3 w-px bg-worker/50"
                    style={{ left: `${v}%` }} />
                ))}
              </div>
              <p className="text-xs text-smoke mt-2 font-mono">
                {t("adminX.dashboard.used", {
                  pct: budget.spent_pct, remaining: budget.remaining_usd,
                })}
              </p>
              {steps && (
                <p className="text-[11px] text-smoke mt-1 font-mono break-words" data-testid="ai-ceiling-steps">
                  {t("adminX.aiUsage.stepLevel")}:{" "}
                  <strong className="text-worker">{steps.level ? step(steps.level) : "—"}</strong>
                  {Object.keys(steps.reached).length > 0 && (
                    <>
                      {" · "}
                      {Object.entries(steps.reached)
                        .map(([s, at]) => `${step(s)} ${dayLabel(at)}`)
                        .join(", ")}
                    </>
                  )}
                  {" · "}{t("adminX.aiUsage.resumeOn", { date: dayLabel(steps.resume_on) })}
                </p>
              )}
              {/* The daily 06:00 UTC projection (DY1.7 M3), as stored — the
                  figure its alert quoted. Unpriced calls are named beside it
                  because they make every number here optimistic. */}
              {burn && (
                <p className={`text-[11px] mt-1 font-mono break-words ${burn.over ? "text-red-700" : "text-smoke"}`}
                   data-testid="ai-burn-rate">
                  {t("adminX.aiUsage.projection")}:{" "}
                  <strong>${burn.projection_usd}</strong>
                  {" · "}{t("adminX.aiUsage.daily")} ${burn.daily_usd}
                  {burn.unpriced_calls > 0 && (
                    <>{" · "}{t("adminX.aiUsage.unpricedCalls", { n: burn.unpriced_calls })}</>
                  )}
                  {" · "}{String(burn.computed_at).slice(0, 16).replace("T", " ")} UTC
                </p>
              )}
            </div>

            {/* The selected window. */}
            <div className="card-base p-5" data-testid="ai-period-totals">
              <p className="label-base mb-0">{t("adminX.aiUsage.spentInPeriod")}</p>
              <p className="font-mono text-3xl font-bold text-worker mt-1">
                ${data.spent_usd.toFixed(4)}
              </p>
              <p className="text-xs text-smoke mt-2 font-mono">
                {t("adminX.dashboard.calls", { n: data.calls_total })}
                {" · "}
                {rangeLabel(data.period)}
              </p>
              {data.calls_cost_unknown > 0 && (
                <p className="text-xs text-amber-800 mt-2" data-testid="ai-unpriced">
                  <AlertTriangle size={12} className="inline mr-1" />
                  {t("adminX.aiUsage.unpriced", { n: data.calls_cost_unknown })}
                </p>
              )}
            </div>
          </div>

          <div className={`mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
            <Breakdown
              title={t("adminX.aiUsage.byChannel")} rows={data.by_channel}
              nameOf={(r) => t(`adminX.aiUsage.channel.${r.channel}`, { defaultValue: r.channel })}
              keyOf={(r) => r.channel} total={data.spent_usd} t={t} testid="channel"
            />
            <Breakdown
              title={t("adminX.dashboard.callsByFeature")} rows={data.by_feature}
              nameOf={(r) => r.feature.replace(/_/g, " ")}
              keyOf={(r) => r.feature} total={data.spent_usd} t={t} testid="feature"
            />
          </div>

          <TopUsers users={data.top_users} t={t} loading={loading} />
        </>
      )}

      <MonthlySeries series={series} t={t} />
    </div>
  );
}

function rangeLabel(period) {
  if (!period) return "";
  return `${dayLabel(period.start)} → ${dayLabel(period.end)}`;
}

/**
 * One breakdown list. The share bar is a magnitude encoding of the same number
 * printed beside it — a second read of one value, not a second value — so it
 * takes the one series hue and carries no legend.
 */
function Breakdown({ title, rows, nameOf, keyOf, total, t, testid }) {
  const max = Math.max(...(rows || []).map((r) => r.spent), 0);
  return (
    <div className="card-base p-5">
      <p className="label-base mb-3 flex items-center gap-1">
        <TrendingUp size={12} /> {title}
      </p>
      {!rows || rows.length === 0 ? (
        <p className="text-xs text-smoke italic">{t("adminX.dashboard.noUsage")}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={keyOf(r)} data-testid={`${testid}-row-${keyOf(r)}`}>
              <div className="flex items-center justify-between text-sm gap-3">
                <span className="text-worker capitalize truncate">{nameOf(r)}</span>
                <span className="font-mono text-smoke whitespace-nowrap">
                  {r.calls} · ${r.spent.toFixed(4)}
                </span>
              </div>
              <div className="mt-1 h-1 w-full bg-sand/60 rounded-full overflow-hidden">
                <div
                  className="h-full bg-hive-ink rounded-full"
                  style={{ width: max > 0 ? `${(r.spent / max) * 100}%` : "0%" }}
                />
              </div>
            </div>
          ))}
          <p className="text-[11px] text-smoke font-mono pt-1">
            {t("adminX.aiUsage.sumsTo", { total: total.toFixed(4) })}
          </p>
        </div>
      )}
    </div>
  );
}

function TopUsers({ users, t, loading }) {
  return (
    <div className={`card-base p-5 mt-4 transition-opacity ${loading ? "opacity-50" : ""}`}>
      <p className="label-base mb-3">{t("adminX.dashboard.topUsers")}</p>
      {!users || users.length === 0 ? (
        <p className="text-xs text-smoke italic">{t("adminX.dashboard.noTopUsers")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-users-table">
            <thead>
              <tr className="text-left text-xs uppercase text-smoke font-display">
                <th className="py-2">{t("adminX.dashboard.thUser")}</th>
                <th>{t("adminX.dashboard.thRole")}</th>
                <th className="text-right">{t("adminX.dashboard.thCalls")}</th>
                <th className="text-right">{t("adminX.dashboard.thSpent")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-sand" data-testid={`top-user-${u.user_id}`}>
                  <td className="py-2">
                    <p className="text-worker">{u.full_name || u.email || u.user_id}</p>
                    <p className="text-xs text-smoke font-mono truncate max-w-[22rem]">
                      {u.email || u.user_id}
                    </p>
                  </td>
                  <td className="text-xs text-smoke">
                    {u.role === "provider"
                      ? t("adminX.dashboard.providerSuffix", { n: u.tier_priority })
                      : u.role || "—"}
                  </td>
                  <td className="text-right font-mono">{u.calls}</td>
                  <td className="text-right font-mono">${u.spent.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Spend per month since the first row, with the ceiling as a reference line.
 *
 * **Bars and not a line.** A month is a bucket you compare against a ceiling,
 * not a continuous signal you interpolate between — and with two months on
 * record a line would be a single segment pretending to be a trend.
 *
 * **One hue, so no legend** — the title names the series. The ceiling is a
 * different MARK (a dashed rule with its value written on it), not a second
 * colour, which is what keeps the two readable for a viewer who cannot separate
 * hues: the `dataviz` validator puts brand gold #E8C400 at 1.66:1 against this
 * card, so the bars use `hive-ink` #6B5600 — the palette's own legible gold,
 * which passes contrast ≥3:1.
 *
 * **The table is not a fallback, it is the other half.** Four significant
 * decimal places do not survive being a bar, and this is a bill.
 */
const PLOT_HEIGHT = 180;

function MonthlySeries({ series, t }) {
  const [view, setView] = useState("chart");
  const [hover, setHover] = useState(null);

  const months = series?.months || [];
  const budget = series?.budget_usd || 0;
  // Headroom so the ceiling rule is never flush with the top edge, and so a
  // month that exceeds it still has somewhere to be drawn.
  const ceiling = useMemo(
    () => Math.max(budget, ...months.map((m) => m.spent), 0.0001) * 1.15,
    [budget, months],
  );

  if (!series) {
    return (
      <div className="card-base p-8 mt-4 flex justify-center">
        <Loader2 className="animate-spin text-smoke" />
      </div>
    );
  }

  return (
    <div className="card-base p-5 mt-4" data-testid="ai-monthly-series">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <p className="label-base mb-0">{t("adminX.aiUsage.historyTitle")}</p>
        <div className="flex gap-1">
          {["chart", "table"].map((v) => (
            <button
              key={v} onClick={() => setView(v)}
              className={`px-2 py-1 rounded-sm text-[11px] font-display font-semibold ${
                view === v ? "bg-worker text-cream"
                           : "bg-cream border border-sand text-worker hover:bg-linen"}`}
              data-testid={`ai-series-view-${v}`}
            >
              {t(`adminX.aiUsage.view.${v}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-smoke mb-4">{t("adminX.aiUsage.historyHint")}</p>

      {months.length === 0 && (
        <p className="text-xs text-smoke italic">{t("adminX.dashboard.noUsage")}</p>
      )}

      {months.length > 0 && view === "chart" && (
        <div data-testid="ai-series-chart">
          {/* ONE plot box. The bars and the ceiling rule are both positioned
              against it, so a bar at 50% and a rule at 50% land on the same
              line — which is the whole job of a reference line and is exactly
              what breaks when the two are measured against different boxes
              (a padded parent, a flex child with its own height). */}
          <div className="relative" style={{ height: PLOT_HEIGHT }}>
            {budget > 0 && (
              <div
                className="absolute inset-x-0 border-t-2 border-dashed border-worker/45 pointer-events-none z-10"
                style={{ bottom: `${(budget / ceiling) * 100}%` }}
                data-testid="ai-series-ceiling"
              >
                <span className="absolute -top-2.5 right-0 text-[10px] font-mono text-worker/70 bg-linen px-1 leading-none">
                  {t("adminX.aiUsage.ceiling", { budget })}
                </span>
              </div>
            )}

            <div className="absolute inset-0 flex items-end gap-[2px] overflow-x-auto">
              {months.map((m) => {
                const pct = (m.spent / ceiling) * 100;
                const active = hover === m.month;
                return (
                  <div
                    key={m.month}
                    className="relative flex-1 min-w-[28px] max-w-[72px] h-full flex flex-col justify-end cursor-default outline-none"
                    onMouseEnter={() => setHover(m.month)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(m.month)}
                    onBlur={() => setHover(null)}
                    tabIndex={0}
                    data-testid={`ai-series-bar-${m.month}`}
                    aria-label={`${monthLabel(m.month)}: $${m.spent.toFixed(4)}, ${m.calls}`}
                  >
                    {active && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap
                                      bg-worker text-cream text-[11px] font-mono rounded-sm px-2 py-1 shadow-lg">
                        {monthLabel(m.month)} · ${m.spent.toFixed(4)} · {m.calls}
                        {m.calls_cost_unknown > 0
                          ? ` · ${t("adminX.aiUsage.unpricedShort", { n: m.calls_cost_unknown })}`
                          : ""}
                      </div>
                    )}
                    {/* A month with zero spend still gets a 2px stub, so "no
                        spend" and "no month" are different marks. */}
                    <div
                      className={`w-full rounded-t bg-hive-ink transition-opacity ${active ? "opacity-100" : "opacity-85"}`}
                      style={{ height: m.spent > 0 ? `max(${pct}%, 3px)` : "2px" }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* The month labels ride in their own row rather than absolutely
              inside the plot: inside, they either overlap a short bar or push
              the plot box taller than the value it is scaled against. */}
          <div className="flex gap-[2px] mt-1.5">
            {months.map((m) => (
              <span key={m.month}
                    className="flex-1 min-w-[28px] max-w-[72px] text-center text-[10px] text-smoke font-mono">
                {monthLabel(m.month)}
              </span>
            ))}
          </div>
        </div>
      )}

      {months.length > 0 && view === "table" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-series-table">
            <thead>
              <tr className="text-left text-xs uppercase text-smoke font-display">
                <th className="py-2">{t("adminX.aiUsage.thMonth")}</th>
                <th className="text-right">{t("adminX.dashboard.thCalls")}</th>
                <th className="text-right">{t("adminX.dashboard.thSpent")}</th>
                <th className="text-right">{t("adminX.aiUsage.thOfCeiling")}</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m) => (
                <tr key={m.month} className="border-t border-sand">
                  <td className="py-2 font-mono">{monthLabel(m.month)}</td>
                  <td className="text-right font-mono">{m.calls}</td>
                  <td className="text-right font-mono">${m.spent.toFixed(4)}</td>
                  <td className="text-right font-mono text-smoke">
                    {budget > 0 ? `${((m.spent / budget) * 100).toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** `2026-08-01` -> `2026-08`. Sliced, not parsed: `new Date("2026-08-01")` is
 *  UTC midnight and renders as July in any negative offset, which is how a
 *  chart ends up labelling its own months one off. */
function monthLabel(iso) {
  return String(iso).slice(0, 7);
}
